/**
 * AdminReferrals — Admin Hub Referrals management tab
 *
 * Tabs:
 *  - Pending Approval — review prospect submissions
 *  - Disputed — allocate disputed referrals
 *  - Commission Tracking — view + update commission statuses
 *  - Overview — summary stats + referral tree
 * F-138
 */

import { useState, useEffect, useCallback } from 'react';
import { getAPIBase, getAuthHeaders } from '../lib/api-fetch';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  CheckCircle2, XCircle, Users, TrendingUp, DollarSign,
  Download, Loader2, RefreshCw, AlertCircle, ChevronDown, ChevronRight as ChevronRightIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

interface Prospect {
  prospectId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  currentAgency: string;
  monthlyDealVolume: number;
  commissionSplit: string;
  notes: string;
  status: string;
  submittedAt: string;
  referringAgentId: string;
}

interface Commission {
  commissionId: string;
  referringAgentId: string;
  referredAgentId: string;
  dealId: string;
  grossCommission: number;
  incentiveRate: number;
  incentiveAmount: number;
  status: string;
  earnedAt: string;
  paidAt: string | null;
  adminNote: string | null;
}

function formatZAR(amount: number): string {
  return `R ${(amount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Pending approval tab ─────────────────────────────────────────────────────

function PendingTab() {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${getAPIBase()}/admin/referrals/pending?status=pending_review`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setProspects(json.data.prospects || []);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (prospectId: string) => {
    setActionId(prospectId);
    try {
      const res = await fetch(`${getAPIBase()}/admin/referrals/${prospectId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Referral approved — agent provisioned');
        load();
      } else {
        toast.error(json.message || 'Approval failed');
      }
    } catch {
      toast.error('Approval failed');
    } finally {
      setActionId(null);
    }
  };

  const reject = async () => {
    if (!rejectId || !rejectReason.trim()) return;
    setActionId(rejectId);
    try {
      const res = await fetch(`${getAPIBase()}/admin/referrals/${rejectId}/reject`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Referral rejected');
        setRejectId(null);
        setRejectReason('');
        load();
      } else {
        toast.error(json.message || 'Rejection failed');
      }
    } catch {
      toast.error('Rejection failed');
    } finally {
      setActionId(null);
    }
  };

  if (isLoading) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div>;

  if (prospects.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <CheckCircle2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <p className="text-sm font-medium">No pending referrals</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {prospects.map(p => (
          <div key={p.prospectId} className="border border-border rounded-xl p-4 bg-muted/30" data-testid={`referral-prospect-${p.prospectId}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-semibold text-foreground">{p.firstName} {p.lastName}</p>
                  <Badge variant="secondary" className="text-xs">{p.status}</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{p.email} · {p.phone}</p>
                <div className="grid grid-cols-3 gap-3 mt-3 text-xs text-foreground">
                  <div><span className="text-muted-foreground">Agency</span><br />{p.currentAgency}</div>
                  <div><span className="text-muted-foreground">Deal Vol.</span><br />{p.monthlyDealVolume}/mo</div>
                  <div><span className="text-muted-foreground">Split</span><br />{p.commissionSplit}</div>
                </div>
                {p.notes && <p className="text-xs text-muted-foreground mt-2 italic">"{p.notes}"</p>}
                <p className="text-xs text-muted-foreground mt-2">
                  Submitted {new Date(p.submittedAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={actionId === p.prospectId}
                  onClick={() => approve(p.prospectId)}
                  data-testid={`approve-${p.prospectId}`}
                >
                  {actionId === p.prospectId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive border-destructive/30 hover:bg-destructive/10"
                  disabled={actionId === p.prospectId}
                  onClick={() => { setRejectId(p.prospectId); setRejectReason(''); }}
                  data-testid={`reject-${p.prospectId}`}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejectId} onOpenChange={() => setRejectId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reject Referral</DialogTitle>
            <DialogDescription>Provide a reason — it will be sent to the prospect.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <textarea
              rows={3}
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setRejectId(null)}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={reject} disabled={!rejectReason.trim()}>
                Confirm Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Commission tracking tab ──────────────────────────────────────────────────

function CommissionsTab() {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [actionId, setActionId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const url = `${getAPIBase()}/admin/referrals/commissions${statusFilter ? `?status=${statusFilter}` : ''}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) {
        setCommissions(json.data.commissions || []);
        setSummary(json.data.summary);
      }
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (commissionId: string, status: string) => {
    setActionId(commissionId);
    try {
      const res = await fetch(`${getAPIBase()}/admin/referrals/commissions/${commissionId}/status`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success(`Commission marked as ${status}`);
        load();
      }
    } catch {
      toast.error('Update failed');
    } finally {
      setActionId(null);
    }
  };

  const exportCSV = async () => {
    try {
      const res = await fetch(`${getAPIBase()}/admin/referrals/commissions/export`, { headers: getAuthHeaders() });
      const text = await res.text();
      const blob = new Blob([text], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `referral-commissions-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  const statusColor: Record<string, string> = {
    held: 'bg-amber-500/10 text-amber-400',
    payable: 'bg-emerald-500/10 text-emerald-400',
    overdue: 'bg-destructive/10 text-destructive',
    paid: 'bg-primary/10 text-primary',
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Held', value: summary.held, color: 'text-amber-400' },
            { label: 'Payable', value: summary.payable, color: 'text-emerald-400' },
            { label: 'Overdue', value: summary.overdue, color: 'text-destructive' },
            { label: 'Paid', value: summary.paid, color: 'text-primary' },
          ].map(({ label, value, color }) => (
            <div key={label} className="p-3 bg-muted rounded-lg text-center">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className={`font-bold ${color}`}>{formatZAR(value)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex gap-2">
          {['', 'held', 'payable', 'overdue', 'paid'].map(s => (
            <button
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                statusFilter === s ? 'bg-foreground text-background border-foreground' : 'bg-muted text-muted-foreground border-border hover:bg-muted/80'
              }`}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} data-testid="admin-referral-export">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div>
      ) : commissions.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No commissions found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs text-muted-foreground">
                <th className="text-left pb-2 font-medium">Referring Agent</th>
                <th className="text-left pb-2 font-medium">Referred Agent</th>
                <th className="text-right pb-2 font-medium">Gross Comm.</th>
                <th className="text-right pb-2 font-medium">Incentive</th>
                <th className="text-center pb-2 font-medium">Status</th>
                <th className="text-right pb-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {commissions.map(c => (
                <tr key={c.commissionId} data-testid={`commission-${c.commissionId}`}>
                  <td className="py-2 text-xs font-mono text-muted-foreground">{c.referringAgentId.slice(-8)}</td>
                  <td className="py-2 text-xs font-mono text-muted-foreground">{c.referredAgentId.slice(-8)}</td>
                  <td className="py-2 text-right">{formatZAR(c.grossCommission)}</td>
                  <td className="py-2 text-right font-semibold">{formatZAR(c.incentiveAmount)}</td>
                  <td className="py-2 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[c.status] || 'bg-gray-100 text-gray-600'}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    {c.status !== 'paid' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        disabled={actionId === c.commissionId}
                        onClick={() => updateStatus(c.commissionId, 'paid')}
                      >
                        {actionId === c.commissionId ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Mark Paid'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Referral tree node ───────────────────────────────────────────────────────

interface TreeNode {
  agentId: string;
  agentName: string | null;
  status: string;
  referralId?: string | null;
  onboardingDate: string | null;
  dealsCount: number;
  totalCommissionGenerated: number;
  referredBy: string | null;
  children: TreeNode[];
  isProspect?: boolean;
}

const statusTreeColor: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  pending_review: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  approved: 'bg-primary/10 text-primary border-primary/20',
  rejected: 'bg-destructive/10 text-destructive border-destructive/20',
  inactive: 'bg-muted text-muted-foreground border-border',
};

function TreeNodeRow({ node, depth = 0 }: { node: TreeNode; depth?: number }) {
  const [open, setOpen] = useState(depth < 2);
  const hasChildren = node.children.length > 0;
  const st = statusTreeColor[node.status] ?? 'bg-muted text-muted-foreground border-border';
  const initials = (node.agentName || '?').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div>
      <div
        className="flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-muted/40 transition-colors cursor-pointer"
        style={{ paddingLeft: `${12 + depth * 24}px` }}
        onClick={() => hasChildren && setOpen(o => !o)}
      >
        <span className="w-4 shrink-0">
          {hasChildren
            ? (open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRightIcon className="w-3.5 h-3.5 text-muted-foreground" />)
            : null}
        </span>
        <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-semibold text-xs shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground truncate">{node.agentName || 'Unknown'}</span>
            {node.referralId && (
              <span className="text-xs font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded">
                {node.referralId}
              </span>
            )}
            <span className={`text-xs border px-1.5 py-0.5 rounded ${st}`}>
              {node.isProspect ? 'Prospect' : node.status}
            </span>
          </div>
          {!node.isProspect && (node.dealsCount > 0 || node.totalCommissionGenerated > 0) && (
            <p className="text-xs text-muted-foreground">
              {node.dealsCount} deal{node.dealsCount !== 1 ? 's' : ''} · {formatZAR(node.totalCommissionGenerated)} commission
            </p>
          )}
        </div>
        {hasChildren && (
          <span className="text-xs text-muted-foreground shrink-0">{node.children.length}</span>
        )}
      </div>
      {open && hasChildren && (
        <div>
          {node.children.map(child => (
            <TreeNodeRow key={child.agentId} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function OverviewTab() {
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [stats, setStats] = useState<{ totalAgents: number; totalActiveAgents: number; totalProspects: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${getAPIBase()}/admin/referrals/tree`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) {
        setTree(json.data.tree || []);
        setStats({
          totalAgents: json.data.totalAgents || 0,
          totalActiveAgents: json.data.totalActiveAgents || 0,
          totalProspects: json.data.totalProspects || 0,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      {/* Summary */}
      {stats && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total Agents', value: stats.totalAgents, color: 'text-foreground' },
            { label: 'Active Agents', value: stats.totalActiveAgents, color: 'text-emerald-400' },
            { label: 'Prospects', value: stats.totalProspects, color: 'text-amber-400' },
          ].map(({ label, value, color }) => (
            <div key={label} className="p-3 bg-muted rounded-lg text-center">
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className={`text-xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Referral Tree</h3>
        <Button variant="ghost" size="icon" onClick={load} data-testid="admin-ref-tree-refresh">
          <RefreshCw className="w-4 h-4 text-muted-foreground" />
        </Button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div>
      ) : tree.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="font-medium">No referrals yet</p>
          <p className="text-sm mt-1">Referrals will appear here once agents share their links</p>
        </div>
      ) : (
        <div className="border border-border rounded-xl overflow-hidden">
          {tree.map(node => (
            <TreeNodeRow key={node.agentId} node={node} depth={0} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AdminReferrals() {
  return (
    <div className="space-y-4" data-testid="admin-referrals">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-foreground">Referral Management</h2>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="admin-ref-tab-pending">Pending Approval</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="admin-ref-tab-commissions">Commission Tracking</TabsTrigger>
          <TabsTrigger value="overview" data-testid="admin-ref-tab-overview">Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <PendingTab />
        </TabsContent>

        <TabsContent value="commissions" className="mt-4">
          <CommissionsTab />
        </TabsContent>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
